import { useState, useEffect, useCallback } from 'react';
import { Divider, Box } from '@mui/material';
import * as yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import ExamTitleField from './ExamTitleField';
import QuestionTable from './QuestionTable';
import { apiRequest } from '@/src/services';
import { Constants, getZeroOption, type Option } from '@/src/utils';
import {
    Loading,
    SimpleButton,
    SimpleTextField,
    ControlledSingleSelect,
    ControlledMultipleSelect,
    ControlledCheckbox,
    ErrorMessage,
    SimpleSingleSelect,
    ControlledTextField,
} from '@/src/ui';
import type { QtiExamDTO, ExamOptionsDTO, ExamQuestionListItemDTO } from '../models/ExamDTOs';

const schema = yup.object().shape({
    id: yup.number().optional(),
    examTypeMasterId: yup.number().required().not([0], 'Assessment type is required'),
    title: yup
        .string()
        .trim()
        .required('Title is required')
        .max(
            Constants.MAX_CHARS.TITLE,
            `Title cannot exceed ${Constants.MAX_CHARS.TITLE} characters`
        ),
    gradeLevelMasterIds: yup
        .array()
        .of(
            yup
                .number()
                .typeError('Grade id must be a number')
                .moreThan(0, 'Grade id must be greater than 0')
                .required()
        )
        .min(1, 'At least one grade must be selected')
        .required(),
    segments: yup
        .array()
        .of(
            yup.object().shape({
                id: yup.string().required(),
                title: yup.string().required(),
                questions: yup
                    .array()
                    .of(
                        yup.object().shape({
                            id: yup.number().required(),
                            title: yup.string().required(),
                        })
                    )
                    .min(1, 'At least one question must be added')
                    .required(),
                meta: yup
                    .object()
                    .shape({
                        isCalculatorAllowed: yup.boolean().required(),
                    })
                    .required(),
            })
        )
        .min(1, 'At least one segment must be added')
        .required(),
    shouldShuffleQuestions: yup.boolean().required(),
    isSpanishAllowed: yup.boolean().required(),
});

interface ExamEditorProps {
    initialModel?: QtiExamDTO;
}

const SelectExamTypeOption = getZeroOption('Select Assessment Type');
const LoadingOption = getZeroOption('Loading...');

export default function ExamEditor({ initialModel }: ExamEditorProps) {
    const isEdit = !!initialModel;
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isTitleError, setIsTitleError] = useState(false);

    const [examTypeMasterOptions, setexamTypeMasterOptions] = useState<Option[]>([LoadingOption]);
    const [gradeOptions, setGradeOptions] = useState<Option[]>([LoadingOption]);

    const [questionData, setQuestionData] = useState<ExamQuestionListItemDTO[]>([]);
    const [searchString, setSearchString] = useState('');
    const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(0);

    // Default choices for new exam
    const defaultValues: QtiExamDTO = initialModel || {
        examTypeMasterId: 0,
        title: '',
        gradeLevelMasterIds: [],
        segments: [],
        shouldShuffleQuestions: false,
        isSpanishAllowed: false,
    };

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitting },
        setError: setFormError,
        clearErrors,
    } = useForm<QtiExamDTO>({
        defaultValues,
        resolver: yupResolver(schema as any),
        mode: 'onBlur',
    });

    const exam = watch();

    const selectedQuestionIds = exam.segments.flatMap((seg) => seg.questions.map((q) => q.id));

    const availableQuestions = questionData
        .filter((q) => q.title.toLowerCase().trim().includes(searchString.toLowerCase().trim()))
        .filter((q) => !selectedQuestionIds.includes(q.id));

    const refreshTableData = useCallback(async () => {
        if (exam.gradeLevelMasterIds.length === 0) {
            setError('Select a grade first.');
            return;
        }

        setError('');
        setLoading(true);

        setSearchString('');

        let requestUrl = `assessments/questions`;
        let isFirst = true;
        exam.gradeLevelMasterIds.forEach((g) => {
            requestUrl += `${isFirst ? '?' : '&'}gradeLevelMasterIds=${g}`;
            isFirst = false;
        });

        try {
            const res = await apiRequest<ExamQuestionListItemDTO[]>('GET', requestUrl);
            setQuestionData(res.data);
        } catch {
            setError('Failed to fetch question data');
        } finally {
            setLoading(false);
        }
    }, [exam.gradeLevelMasterIds]);

    useEffect(() => {
        (async function init() {
            setLoading(true);
            try {
                const res = await apiRequest<ExamOptionsDTO>(
                    'GET',
                    'utility/filterData?getExamTypeMaster=true&getGradeLevelMaster=true'
                );
                const { examTypeMasterOptions, gradeLevelMasterOptions } = res.data;

                setexamTypeMasterOptions([SelectExamTypeOption, ...examTypeMasterOptions]);
                setGradeOptions(gradeLevelMasterOptions);
            } catch {
                setError('Failed to fetch form options');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Segment-based question actions
    const handleQuestionAction = useCallback(
        (
            row: ExamQuestionListItemDTO,
            actionType: 'add' | 'remove' | 'moveUp' | 'moveDown',
            segmentIdx?: number
        ) => {
            const segments = exam.segments || [];
            if (segmentIdx === undefined || segmentIdx < 0 || segmentIdx >= segments.length) return;
            const segment = segments[segmentIdx];
            const newSegments = [...segments];
            if (actionType === 'add') {
                const newQuestions = [...segment.questions, row];
                newSegments[segmentIdx] = { ...segment, questions: newQuestions };
            } else if (actionType === 'remove') {
                const newQuestions = segment.questions.filter((q) => q.id !== row.id);
                newSegments[segmentIdx] = { ...segment, questions: newQuestions };
            } else if (actionType === 'moveUp') {
                const idx = segment.questions.findIndex((q) => q.id === row.id);
                if (idx > 0) {
                    const newQuestions = [...segment.questions];
                    [newQuestions[idx - 1], newQuestions[idx]] = [
                        newQuestions[idx],
                        newQuestions[idx - 1],
                    ];
                    newSegments[segmentIdx] = { ...segment, questions: newQuestions };
                }
            } else if (actionType === 'moveDown') {
                const idx = segment.questions.findIndex((q) => q.id === row.id);
                if (idx !== -1 && idx < segment.questions.length - 1) {
                    const newQuestions = [...segment.questions];
                    [newQuestions[idx], newQuestions[idx + 1]] = [
                        newQuestions[idx + 1],
                        newQuestions[idx],
                    ];
                    newSegments[segmentIdx] = { ...segment, questions: newQuestions };
                }
            }
            setValue('segments', newSegments);
        },
        [setValue, exam.segments]
    );

    const handleAddSegment = () => {
        const newSegment = {
            id: uuidv4(),
            title: `Segment ${exam.segments.length + 1}`,
            questions: [],
            meta: { isCalculatorAllowed: false },
        };
        setValue('segments', [...exam.segments, newSegment]);
        setSelectedSegmentIndex(exam.segments.length);
    };

    const handleRemoveSegment = (idx: number) => {
        if (exam.segments.length <= 1) return;
        const newSegments = exam.segments.filter((_, i) => i !== idx);
        setValue('segments', newSegments);
        setSelectedSegmentIndex(Math.max(0, idx - 1));
    };

    const onSubmit = async () => {
        if (success) return;
        if (isTitleError) {
            setError('Please choose a unique title.');
            return;
        }

        clearErrors();
        setLoading(true);
        setError(null);

        try {
            const url = isEdit ? `/assessments/exams/${exam.id}` : '/assessments/exams';
            const method = isEdit ? 'PUT' : 'POST';
            await apiRequest(method, url, exam);
            setSuccess(true);
        } catch (e: any) {
            setError(e.message || 'Unknown error');
            setFormError('root', { type: 'manual', message: e.message || 'Unknown error' });
            return;
        } finally {
            setLoading(false);
        }
    };

    return (
        <form key={`ExamEditor`} className="p-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {renderLoaders(loading, success, isSubmitting, error, isEdit)}
            <ExamTitleField
                id={exam.id}
                title={exam.title}
                control={control}
                setIsTitleError={setIsTitleError}
                error={errors.title}
            />

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                <ControlledSingleSelect
                    name="examTypeMasterId"
                    control={control}
                    options={examTypeMasterOptions}
                    label="Type"
                    error={errors.examTypeMasterId}
                    sx={{ flex: 1 }}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                    <ControlledMultipleSelect
                        name="gradeLevelMasterIds"
                        control={control}
                        options={gradeOptions}
                        value={exam.gradeLevelMasterIds}
                        disabled={selectedQuestionIds.length > 0}
                        onChange={(e, field) => {
                            setQuestionData([]);
                            setValue(
                                'segments',
                                exam.segments.map((seg) => ({ ...seg, questions: [] }))
                            );
                            field.onChange(e.target.value);
                        }}
                        label="Grade Level"
                        error={errors.gradeLevelMasterIds}
                    />
                    {selectedQuestionIds.length > 0 && (
                        <div className="text-orange-600">
                            Grade cannot be changed when questions are selected.
                        </div>
                    )}
                </Box>
            </Box>

            <div className="mt-4" />

            <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                <div style={{ flex: 1 }}>
                    <ControlledCheckbox
                        name="shouldShuffleQuestions"
                        label="Shuffle Questions"
                        onChange={(e, field) => {
                            const checked = e.target.checked;
                            field.onChange(checked);
                        }}
                        control={control}
                    />
                    {errors.shouldShuffleQuestions && (
                        <ErrorMessage error={errors.shouldShuffleQuestions.message} />
                    )}
                </div>
                <div style={{ flex: 1 }}>
                    <ControlledCheckbox
                        name="isSpanishAllowed"
                        label="Is Spanish Allowed"
                        onChange={(e, field) => {
                            const checked = e.target.checked;
                            field.onChange(checked);
                        }}
                        control={control}
                    />
                    {errors.isSpanishAllowed && (
                        <ErrorMessage error={errors.isSpanishAllowed.message} />
                    )}
                </div>
            </div>

            <div className="mt-4" />
            <Divider />
            <div className="mt-4" />

            <Box sx={{ mb: 2 }}>
                <h2>Segments</h2>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <SimpleButton
                        onClick={handleAddSegment}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                    >
                        Add Segment
                    </SimpleButton>
                    {exam.segments.length >= 1 && (
                        <>
                            <SimpleButton
                                onClick={() => handleRemoveSegment(selectedSegmentIndex)}
                                variant="outlined"
                                color="secondary"
                                disabled={loading}
                            >
                                Remove Segment
                            </SimpleButton>
                            <h2>Select Segment:</h2>
                            <SimpleSingleSelect
                                name="selectedSegment"
                                label="Select Segment"
                                onChange={(e) => setSelectedSegmentIndex(e.target.value as number)}
                                options={exam.segments.map((seg, idx) => ({
                                    id: idx,
                                    name: seg.title || `Segment ${idx + 1}`,
                                }))}
                                value={selectedSegmentIndex}
                            />
                        </>
                    )}
                </Box>

                {exam.segments[selectedSegmentIndex] && (
                    <Box
                        key={`exam-segment-${selectedSegmentIndex}`}
                        sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}
                    >
                        <ControlledTextField
                            control={control}
                            label="Segment Title"
                            variant="outlined"
                            name={`segments.${selectedSegmentIndex}.title`}
                            error={errors.segments?.[selectedSegmentIndex]?.title}
                        />
                        <ControlledCheckbox
                            name={`segments.${selectedSegmentIndex}.meta.isCalculatorAllowed`}
                            label="Calculator Allowed"
                            control={control}
                            checked={
                                exam.segments[selectedSegmentIndex].meta?.isCalculatorAllowed ??
                                false
                            }
                        />
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    justifyContent: 'center',
                    mb: 2,
                    alignItems: 'center',
                }}
            >
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 1 }}>
                    <SimpleButton
                        onClick={() => {
                            refreshTableData();
                        }}
                        variant="contained"
                        color="primary"
                    >
                        Search
                    </SimpleButton>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'right', mb: 1 }}>
                    <Box sx={{ minWidth: 240, maxWidth: 320 }}>
                        <SimpleTextField
                            label="Search questions"
                            variant="outlined"
                            name="searchString"
                            value={searchString}
                            onChange={(e) => setSearchString(e.target.value)}
                        />
                        <ErrorMessage error={error} style={{ marginTop: 4 }} />
                    </Box>
                </Box>
            </Box>

            <Loading visible={loading} message="Loading questions..." />
            <ErrorMessage error={error} />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'center',
                    mb: 2,
                }}
            >
                <div>
                    <h2>
                        Available Questions (for segment:{' '}
                        {exam.segments[selectedSegmentIndex]?.title ||
                            `Segment ${selectedSegmentIndex + 1}`}
                        )
                    </h2>
                    <QuestionTable
                        data={availableQuestions}
                        onAction={(row) => handleQuestionAction(row, 'add', selectedSegmentIndex)}
                        actionType="add"
                        allowReorder={false}
                    />
                </div>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'center',
                    mb: 2,
                }}
            >
                <h2>Questions by Segment</h2>
                {exam.segments.map((seg, idx) => (
                    <div
                        key={seg.id}
                        style={{
                            marginBottom: 24,
                            border: '1px solid #eee',
                            borderRadius: 8,
                            padding: 12,
                        }}
                    >
                        <h3>{seg.title || `Segment ${idx + 1}`}</h3>
                        <ControlledCheckbox
                            name={`segments.${idx}.meta.isCalculatorAllowed`}
                            label="Calculator Allowed"
                            control={control}
                            checked={seg.meta?.isCalculatorAllowed ?? false}
                            onChange={(e, field) => field.onChange(e.target.checked)}
                        />
                        <QuestionTable
                            data={seg.questions}
                            onAction={(row, actionType) =>
                                handleQuestionAction(row, actionType, idx)
                            }
                            actionType="remove"
                            allowReorder={true}
                        />
                        <ErrorMessage error={errors.segments?.[idx]?.questions} />
                    </div>
                ))}
            </Box>

            <ErrorMessage error={errors.segments} />

            <div className="flex items-center space-x-2">
                <div>
                    <SimpleButton
                        type="submit"
                        color="primary"
                        className="bg-green-600 text-white px-3 py-1 rounded text-xs"
                        disabled={isSubmitting || loading}
                    >
                        {isEdit ? 'Update Exam' : 'Create Exam'}
                    </SimpleButton>
                </div>
            </div>
            <ErrorMessage
                error={Object.keys(errors).length ? 'Please correct the errors to continue' : ''}
            />
            {renderLoaders(loading, success, isSubmitting, error, isEdit)}
        </form>
    );
}

function renderLoaders(
    loading: boolean,
    success: boolean,
    isSubmitting: boolean,
    error: string | null,
    isEdit: boolean
) {
    return (
        <>
            <Loading visible={loading} message="Loading..." size="md" />
            <Loading
                visible={isSubmitting}
                message={isEdit ? 'Updating exam...' : 'Creating exam...'}
                size="md"
            />
            <ErrorMessage error={error} />
            {success && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="exam-success-msg"
                    className="fixed inset-0 flex items-center justify-center z-110 bg-[#00000040]"
                >
                    <div className="bg-white rounded shadow-lg p-6 flex flex-col items-center">
                        <div
                            id="exam-success-msg"
                            className="text-green-700 font-bold text-lg mb-2"
                        >
                            {isEdit ? 'Exam updated!' : 'Exam created!'}
                        </div>
                        <SimpleButton
                            autoFocus
                            type="button"
                            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded"
                            onClick={() => (window.location.href = '/examsAdmin/viewExams')}
                        >
                            OK
                        </SimpleButton>
                    </div>
                </div>
            )}
        </>
    );
}
